import { useEffect, useMemo, useState } from "react";
import { motion, useMotionValue, animate as fmAnimate } from "framer-motion";
import Chapter from "./Chapter";
import { FRAMEWORKS } from "@/lib/data";
import { IDS } from "@/lib/ids";

const SHOTS = [
  {
    id: "requirements",
    label: "Evidence Requirements",
    caption: "One evidence requirement per topic. Satisfy once, cover many.",
    src: "https://customer-assets.emergentagent.com/job_comply-once/artifacts/ptt7tw8u_1-crosswalk-collect-once.jpg",
  },
  {
    id: "dashboard",
    label: "Compliance Control Room",
    caption: "Real-time readiness, per-framework time-to-audit, live heatmap.",
    src: "https://customer-assets.emergentagent.com/job_comply-once/artifacts/wwidvwx9_2-dashboard-readiness.jpg",
  },
  {
    id: "frameworks",
    label: "Frameworks",
    caption: "Every ISO 27001:2022 Annex A control — 93 controls in one map, grouped, filterable.",
    src: "https://customer-assets.emergentagent.com/job_comply-once/artifacts/du7sbf6w_3-frameworks.jpg",
  },
  {
    id: "risks",
    label: "Risk Register",
    caption: "Inherent → residual, categorised, mapped to mitigating controls.",
    src: "https://customer-assets.emergentagent.com/job_comply-once/artifacts/s0tc3t3k_4-risk-register.jpg",
  },
];

const DEMO_FWKS = FRAMEWORKS.slice(0, 8);

export default function ProductProof() {
  const [enabled, setEnabled] = useState(new Set(["iso27001", "soc2", "gdpr", "dpdp"]));

  const toggle = (id) => {
    setEnabled((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  // Readiness math: base 62 + 4 per enabled framework, capped 92
  const readiness = useMemo(() => Math.min(92, 42 + enabled.size * 6), [enabled]);
  const autoSatisfied = useMemo(() => enabled.size * 12 + 4, [enabled]);

  const ringCirc = 2 * Math.PI * 42;
  const ringOffset = ringCirc - (readiness / 100) * ringCirc;

  return (
    <section id="product" className="relative py-14 md:py-20 hairline-t">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <Chapter number="04" label="Product proof" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-5">
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="font-display text-[36px] md:text-[52px] leading-[1.04] tracking-[-0.022em] text-balance"
            >
              Not a slide. <span className="grad-text">The product</span>.
            </motion.h2>
            <p className="mt-5 text-ink-muted text-[15px] leading-relaxed max-w-[46ch]">
              Real screens from NormShift v2. Toggle frameworks on the right and watch readiness and auto-satisfied controls recompute — this is the crosswalk math the app runs on you.
            </p>
          </div>

          <div className="lg:col-span-7">
            <div className="machined p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-ink-muted">Live · Framework toggle</span>
                <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/50 tabular">{enabled.size}/8 enabled</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[auto,1fr] gap-8 items-center">
                {/* ring — SVG for the arc; HTML overlay for the number so it renders reliably */}
                <div className="relative w-[160px] h-[160px] flex items-center justify-center mx-auto md:mx-0">
                  <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
                    <circle cx="50" cy="50" r="42" fill="none" stroke="var(--hairline-strong)" strokeWidth="6" />
                    <motion.circle
                      cx="50" cy="50" r="42" fill="none"
                      stroke="url(#gradRing)"
                      strokeWidth="6"
                      strokeLinecap="round"
                      strokeDasharray={ringCirc}
                      animate={{ strokeDashoffset: ringOffset }}
                      transition={{ duration: 0.55, ease: [0.22,1,0.36,1] }}
                      transform="rotate(-90 50 50)"
                    />
                    <defs>
                      <linearGradient id="gradRing" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#60A5FA" />
                        <stop offset="60%" stopColor="#6366F1" />
                        <stop offset="100%" stopColor="#E82532" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="relative z-10 flex flex-col items-center justify-center pointer-events-none">
                    <ReadinessNumber value={readiness} />
                    <span
                      className="font-mono text-[9px] tracking-[0.24em] uppercase mt-1"
                      style={{ color: "var(--muted)" }}
                    >
                      Audit ready
                    </span>
                  </div>
                </div>

                <div>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {DEMO_FWKS.map((f) => {
                      const on = enabled.has(f.id);
                      return (
                        <button
                          key={f.id}
                          data-testid={IDS.frameworkToggle(f.id)}
                          onClick={() => toggle(f.id)}
                          className={`px-3 py-1.5 rounded-full border font-mono text-[11px] tracking-[0.04em] transition-[transform,border-color,background,color] duration-300 ${on ? "border-white/25 bg-white/[0.06] text-white" : "border-white/10 bg-transparent text-white/45 hover:text-white/70"}`}
                        >
                          {f.name}
                        </button>
                      );
                    })}
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/8">
                    <Stat label="Auto-satisfied" value={autoSatisfied} />
                    <Stat label="Frameworks live" value={enabled.size} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Featured shot + row of three */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <FeaturedShot shot={SHOTS[0]} className="lg:col-span-8 aspect-[16/10]" />
          <FeaturedShot shot={SHOTS[1]} className="lg:col-span-4 aspect-[4/3] lg:aspect-auto" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <FeaturedShot shot={SHOTS[2]} className="aspect-[16/10]" />
          <FeaturedShot shot={SHOTS[3]} className="aspect-[16/10]" />
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink-muted">{label}</div>
      <div className="font-display text-[28px] mt-1 tabular">{value}</div>
    </div>
  );
}

/** Animated readiness number — rAF-bound so toggling frameworks visibly counts up.  */
function ReadinessNumber({ value }) {
  const mv = useMotionValue(value);
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    const ctrl = fmAnimate(mv, value, {
      duration: 0.55,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => ctrl.stop();
  }, [value, mv]);

  return (
    <span
      data-testid="readiness-percent"
      className="font-display tabular leading-none"
      style={{ fontSize: "30px", color: "var(--text)", letterSpacing: "-0.02em" }}
    >
      {display}
      <span className="text-[16px] ml-0.5" style={{ color: "var(--muted)" }}>%</span>
    </span>
  );
}

function FeaturedShot({ shot, className }) {
  return (
    <motion.figure
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.7, ease: [0.22,1,0.36,1] }}
      className={`screenshot-shot ${className}`}
    >
      {/* Browser chrome */}
      <div className="absolute top-0 inset-x-0 z-10 h-8 bg-[#0B0F1D]/95 border-b border-white/8 flex items-center gap-2 px-3">
        <span className="w-2 h-2 rounded-full bg-white/15" />
        <span className="w-2 h-2 rounded-full bg-white/15" />
        <span className="w-2 h-2 rounded-full bg-white/15" />
        <span className="ml-3 font-mono text-[10px] tracking-[0.18em] uppercase text-white/50 truncate">app.normshift.io — {shot.label}</span>
      </div>
      <img src={shot.src} alt={shot.label} loading="lazy" className="w-full h-full object-cover object-top pt-8" />
      <figcaption className="absolute inset-x-0 bottom-0 p-4 pt-10 z-10 bg-gradient-to-t from-black/85 via-black/40 to-transparent">
        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/60">{shot.label}</div>
        <div className="text-[13px] text-white/90 mt-1 max-w-[52ch]">{shot.caption}</div>
      </figcaption>
    </motion.figure>
  );
}
